import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Route as RouteIcon } from 'lucide-react';
import { fmt, Spinner, ErrorBanner, SABadge } from '../components/ReportTableKit';
import JourneyDetailDrawer from '../components/JourneyDetailDrawer';

const API = process.env.REACT_APP_BACKEND_URL;
const AUDIENCE = { maq: 'MAQ', psh: 'EAS Partner Skilling Hub (PSH)' };
const linkBtn = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#4338ca', fontWeight: 600, fontSize: 13, textAlign: 'left', textDecoration: 'underline', textUnderlineOffset: 2 };

function Tile({ k, label, value }) {
  return (
    <div className="stat-card" data-testid={`lj-tile-${k}`} style={{ padding: 16 }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#64748b', fontWeight: 600 }}>{label}</div>
      <div className="stat-value" style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginTop: 4 }}>{value}</div>
    </div>
  );
}

export default function LearningJourneysFeed({ consumer, enrolPath }) {
  const navigate = useNavigate();
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);

  useEffect(() => {
    setLoading(true); setErr(null);
    axios.get(`${API}/api/feeds/${consumer}/learning-journeys`, { withCredentials: true })
      .then(r => setJourneys(r.data?.data || []))
      .catch(e => setErr(e.response?.status === 403 ? 'This page is restricted to Technofocus users.' : 'The Learning Journeys feed is temporarily unavailable. Please try again shortly.'))
      .finally(() => setLoading(false));
  }, [consumer]);

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? journeys.filter(j => [j.name, j.solution_area, j.target_audience].some(v => String(v || '').toLowerCase().includes(s))) : journeys;
  }, [journeys, q]);
  const areas = new Set(journeys.map(j => j.solution_area).filter(Boolean)).size;
  const acts = journeys.reduce((n, j) => n + (j.activity_count || 0), 0);

  return (
    <div data-testid={`lj-page-${consumer}`}>
      <div className="page-header">
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }} data-testid="lj-title"><RouteIcon size={22} color="#4338ca" /> Learning Journeys</h1>
        <p>Live mirror of the Learning Journeys feed delivered by LevelUp to {AUDIENCE[consumer]}. Click a journey to see its phases, activities and courses.</p>
      </div>
      {!loading && !err && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 12, marginBottom: 20 }}>
          <Tile k="journeys" label="Journeys" value={fmt(journeys.length)} />
          <Tile k="areas" label="Solution Areas" value={fmt(areas)} />
          <Tile k="activities" label="Activities" value={fmt(acts)} />
        </div>
      )}
      <div className="filter-bar">
        <div className="filter-group">
          <label className="filter-label">Search</label>
          <input className="filter-input" style={{ minWidth: 320 }} placeholder="Journey, solution area, audience…" value={q} onChange={e => setQ(e.target.value)} data-testid="lj-search" />
        </div>
      </div>
      <div className="table-container">
        {loading ? <Spinner /> : err ? <ErrorBanner message={err} testId="lj-error" /> : shown.length === 0 ? (
          <div className="empty-state" data-testid="lj-empty"><h3>No journeys found</h3></div>
        ) : (
          <div className="table-scroll">
            <table data-testid="lj-table">
              <thead><tr>
                <th>Journey</th><th>Solution Area</th><th>Audience</th><th className="td-right">Duration</th>
                <th className="td-right">Phases</th><th className="td-right">Activities</th><th className="td-right">Required</th><th />
              </tr></thead>
              <tbody>
                {shown.map(j => (
                  <tr key={j.id} data-testid={`lj-row-${j.id}`}>
                    <td style={{ maxWidth: 380 }}><button style={linkBtn} onClick={() => setOpen(j)} data-testid={`lj-open-${j.id}`}>{j.name}</button></td>
                    <td><SABadge name={j.solution_area} /></td>
                    <td style={{ fontSize: 12 }}>{j.target_audience || '—'}</td>
                    <td className="td-right" style={{ fontSize: 12 }}>{j.duration != null ? `${j.duration} h` : '—'}</td>
                    <td className="td-right">{fmt(j.phase_count)}</td>
                    <td className="td-right">{fmt(j.activity_count)}</td>
                    <td className="td-right">{fmt(j.required_count)}</td>
                    <td className="td-right">
                      <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => navigate(`${enrolPath}?journey=${j.id}`)} data-testid={`lj-enrol-${j.id}`}>Enrolments</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {open && <JourneyDetailDrawer journey={open} onClose={() => setOpen(null)} onViewEnrolments={(id) => navigate(`${enrolPath}?journey=${id}`)} />}
    </div>
  );
}
